import { useDetailedPricing } from '@hooks/use-detailed-pricing';
import { useAppSelector } from '@redux/hooks';
import RfqModal from '@tkmp/rfq/components/RfqModal/RfqModal';
import { useInlineRfqForm } from '@tkmp/rfq/hooks/useInlineRfqForm/useInlineRfqForm';
import { generateDefaultField } from '@tkmp/rfq/utils';
import { Link } from '@tkww/orion-web-ds-action';
import { Icon, type IconName } from '@tkww/orion-web-ds-graphic';
import { TextBody } from '@tkww/orion-web-ds-text';
import type { SecondaryCTASourceContent } from '@typings/sourceContent';
import React, { type FC } from 'react';
import Styles from './SecondaryCta.scss';

export type SecondaryCTAProps = {
	icon: IconName;
	headerText: string;
	linkText: string;
	placeholderRfqText: string;
	sourceContent: SecondaryCTASourceContent;
	className?: string;
};

const SecondaryCTA: FC<SecondaryCTAProps> = (props) => {
	const {
		icon,
		headerText,
		linkText,
		placeholderRfqText,
		sourceContent,
		className = '',
	} = props;

	const conversations = useAppSelector(
		(state) => state.messagedVendors.conversations,
	);
	const vendorId = useAppSelector((state) => state.vendor.vendor?.id) || '';
	const conversationStarted = Boolean(conversations[vendorId || '']);
	const isInDetailedPricing = useDetailedPricing();

	const localFormContext = useInlineRfqForm({
		freeTextLabel: placeholderRfqText,
		headerText: isInDetailedPricing
			? 'Start the convo'
			: 'Start a conversation',
		textAreaInitialValueOverride: generateDefaultField('textarea'),
		initiator: sourceContent,
	});

	return (
		<>
			<RfqModal context={localFormContext} />
			{!conversationStarted && (
				<div className={className}>
					<div className={Styles['secondary-cta-container']}>
						<div className={Styles.circle}>
							<Icon
								className={Styles.icon}
								name={icon}
								size="lg"
								aria-label="Call to action"
							/>
						</div>
						<div className={Styles.textContainer}>
							<TextBody size="md" className={Styles.header} bold>
								{headerText}
							</TextBody>
							<Link
								as="button"
								onClick={localFormContext.openModal}
								className={Styles['action-button']}
							>
								{linkText}
							</Link>
						</div>
					</div>
				</div>
			)}
		</>
	);
};

export default SecondaryCTA;
